import React, { useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import WarningOutlinedIcon from '@material-ui/icons/WarningOutlined';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import { RMSIconButton, RMSButton } from '../buttons/index';
import RMSChip from '../chips/RMSChip';

function CommonNoteCard({
  title,
  description,
  isAlert = false,
  headerText,
  headerActions = [],
  status,
  footer,
  footerActions = [],
}) {
  const [showFooter, setShowFooter] = useState(false);

  return (
    <Box className={`note-card ${isAlert ? 'note-card--alert' : ''}`}>
      <Box className="note-card__header">
        <Box className="note-card__title-section">
          {isAlert && (
            <RMSChip
              icon={<WarningOutlinedIcon />}
              label="ALERT"
              className="note-card__alert-chip"
              size="small"
            />
          )}

          <Typography className="note-card__title">{title}</Typography>

          {status && (
            <RMSChip label={status.label} color={status.color} size="small" variant="outlined" />
          )}
        </Box>

        <Box className="note-card__actions">
          {headerText && (
            <Typography variant="caption" className="note-card__date">
              {headerText}
            </Typography>
          )}

          {headerActions.map((action, index) => (
            <RMSIconButton
              key={action.key || index}
              className={`note-card__icon-btn ${action.className || ''}`}
              icon={action.icon}
              tooltip={action.tooltip}
              label={action.label}
              color={action.color}
              disabled={action.disabled}
              onClick={action.onClick}
            />
          ))}

          {footerActions.length > 0 && (
            <RMSIconButton
              className="note-card__icon-btn"
              icon={showFooter ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
              tooltip={showFooter ? 'Collapse' : 'Expand'}
              onClick={() => setShowFooter((prev) => !prev)}
            />
          )}
        </Box>
      </Box>

      {description && (
        <Box className="note-card__body">
          <Typography className="note-card__description">{description}</Typography>
        </Box>
      )}

      <Box className="note-card__footer">
        {footer && <Box className="note-card__footer-info">{footer}</Box>}

        {showFooter && footerActions.length > 0 && (
          <Box className="note-card__button-group">
            {footerActions.map((item) => (
              <RMSButton
                key={item.label}
                label={item.label}
                size="small"
                variant="outlined"
                onClick={item.onClick}
                disabled={item.disabled}
                className={`note-card__action-btn note-card__action-btn--${
                  item.color || 'primary'
                }`}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default CommonNoteCard;

/**
 *
 *
 * Available Props:
 *
 * title: string
 * description: string
 * isAlert: boolean
 *
 * headerText: string
 *
 * headerActions: [
 *   {
 *     icon: ReactNode,
 *     onClick: () => void,
 *     tooltip: string
 *     disabled: boolean
 *   }
 * ]
 *
 * status: {
 *   label: string,
 *   color:
 *     "default" |
 *     "primary" |
 *     "secondary" |
 *     "success" |
 *     "error" |
 *     "warning" |
 *     "info"
 * }
 *
 * footer: ReactNode
 *
 * footerActions: [
 *   {
 *     label: string,
 *     color:
 *       "primary" |
 *       "secondary" |
 *       "success" |
 *       "error" |
 *       "warning" |
 *       "info" |
 *       "inherit",
 *     onClick: () => void,
 *     disabled?: boolean
 *   }
 * ]
 *
 *
 * Example:
 *
 * import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
 * import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
 * import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
 *
 * <CommonNoteCard
 *   title="Documentation Required"
 *   description="Please upload the missing documents."
 *   isAlert={false}
 *   headerText="06/23/2026, 08:12 PM"
 *   status={{
 *     label: "Open",
 *     color: "error",
 *   }}
 *   footer={
 *     <>
 *       <Typography variant="caption">
 *         Requested By: PTS, Mister • 06/23/2026, 08:12 PM
 *       </Typography>
 *
 *       <Typography variant="caption">
 *         Routed To: Joe Biden
 *       </Typography>
 *     </>
 *   }
 *   headerActions={[
 *     {
 *       icon: <EditOutlinedIcon fontSize="small" />,
 *       onClick: () => console.log("Edit"),
 *     },
 *     {
 *       icon: <DeleteOutlineOutlinedIcon fontSize="small" />,
 *       onClick: () => console.log("Delete"),
 *     },
 *     {
 *       icon: <PrintOutlinedIcon fontSize="small" />,
 *       onClick: () => console.log("Print"),
 *     },
 *   ]}
 *   footerActions={[
 *     {
 *       label: "Mark In Progress",
 *       color: "warning",
 *       onClick: () => console.log("In Progress"),
 *     },
 *     {
 *       label: "Mark Resolved",
 *       color: "success",
 *       onClick: () => console.log("Resolved"),
 *     },
 *   ]}
 * />
 */
